import { Static, TLiteral, Type } from '@sinclair/typebox';

/**
 * Schémas TypeBox de la page admin « Migration abonnement » (v1→v2).
 * Source de vérité unique (validation backend + Swagger + types front via Orval).
 * Cf. docs/to_implement/subscription-migration-admin-page-plan.md (§2, §2bis).
 *
 * Convention : montants en **centimes** (`*_minor`, éditables côté admin) en entiers ;
 * dates en ISO. Le formatage locale/devise pour affichage se fait côté front (outil admin).
 */

// ───────────────────────────── Valeurs énumérées (source unique back + front) ─────────────────────────────

/** Classification d'un abonnement Stripe v1 non-formule. */
export const V1_SUB_KIND = { DOMAIN: 'DOMAIN', UNKNOWN: 'UNKNOWN' } as const;
export type V1SubKind = (typeof V1_SUB_KIND)[keyof typeof V1_SUB_KIND];

/** Issue de l'annulation d'un abonnement v1 (exécutée / échec / volontairement sautée). */
export const V1_CANCEL_STATUS = { OK: 'OK', FAILED: 'FAILED', SKIPPED: 'SKIPPED' } as const;
export type V1CancelStatus = (typeof V1_CANCEL_STATUS)[keyof typeof V1_CANCEL_STATUS];

/** Formules v2 proposables à la migration. */
export const V2_PLAN_LABEL = { STARTER: 'STARTER', PRO: 'PRO', MAX: 'MAX' } as const;
export type V2PlanLabel = (typeof V2_PLAN_LABEL)[keyof typeof V2_PLAN_LABEL];

/** Tailles d'abonnement v2. */
export const V2_PLAN_SIZE = { SOLO: 'SOLO', TEAM: 'TEAM' } as const;
export type V2PlanSize = (typeof V2_PLAN_SIZE)[keyof typeof V2_PLAN_SIZE];

/** Construit une union TypeBox de littéraux à partir d'un ensemble de valeurs (évite la double déclaration). */
const literalUnion = <T extends string>(values: readonly T[]): TLiteral<T>[] =>
  values.map((v): TLiteral<T> => Type.Literal(v));

// ───────────────────────────── Sous-objets preview ─────────────────────────────

const StripeCustomerSchema = Type.Object({
  id: Type.String(),
  email: Type.Union([Type.String(), Type.Null()]),
  name: Type.Union([Type.String(), Type.Null()]),
});

const CustomerCandidateSchema = Type.Object({
  id: Type.String(),
  email: Type.Union([Type.String(), Type.Null()]),
  name: Type.Union([Type.String(), Type.Null()]),
  created_iso: Type.String(),
  active_subscriptions_count: Type.Integer(),
});

const V1PlanSubscriptionSchema = Type.Object({
  stripe_subscription_id: Type.String(),
  v1_label: Type.Union([Type.Number(), Type.Null()]), // 10/20/30/40
  v1_label_name: Type.Union([Type.String(), Type.Null()]), // STARTER/BOOSTER/ZEN/VTC
  interval: Type.String(), // 'month' | 'year' (brut Stripe, informatif)
  unit_amount_minor: Type.Integer(),
  tax_behavior: Type.String(),
  unit_amount_tax_free_minor: Type.Integer(), // HT dérivé (D1)
  current_period_start_iso: Type.String(),
  current_period_end_iso: Type.String(),
  status: Type.String(),
  is_trial: Type.Boolean(),
});

const V1OtherSubscriptionSchema = Type.Object({
  stripe_subscription_id: Type.String(),
  kind: Type.Union(literalUnion(Object.values(V1_SUB_KIND))),
  label: Type.Union([Type.String(), Type.Null()]),
  unit_amount_minor: Type.Integer(),
  interval: Type.String(),
  current_period_end_iso: Type.String(),
});

const DefaultCardSchema = Type.Object({
  brand: Type.String(),
  last4: Type.String(),
  exp_month: Type.Integer(),
  exp_year: Type.Integer(),
  pm_id: Type.String(),
});

/**
 * Usage v1 des fonctionnalités gatées PRO côté Propelsia (cartes cadeaux, paiement en ligne).
 * Compteurs bruts lus sur la v1 (`GET /pro_migration/internal/plan-usage`) : le seuil qui décide
 * d'une remontée de formule vit dans `suggestV2Plan`, pas ici.
 */
/** Statut du compte Stripe Connect (B2C) d'un pro en v1, tel que porté par la v1 (`ConnectStripeStatus`). */
export enum V1StripeConnectStatus {
  NONE = 'NONE',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
}

export const V1PlanUsageSchema = Type.Object({
  v1_pro_id: Type.Integer(),
  vouchers_total: Type.Integer(),
  vouchers_sold_online: Type.Integer(),
  vouchers_last_12m: Type.Integer(),
  /** Bons encore honorables : ce qui casserait si le pro atterrissait en STARTER. */
  vouchers_still_valid: Type.Integer(),
  vouchers_used: Type.Integer(),
  last_voucher_at: Type.Union([Type.String(), Type.Null()]),
  /** Date lisible (locale/fuseau ops), posée par le backend v2 : le front ne formate jamais. */
  last_voucher_at_formatted: Type.Optional(Type.String()),
  gift_shop_enabled: Type.Boolean(),
  gift_card_payments: Type.Integer(),
  payments_settled: Type.Integer(),
  payments_last_12m: Type.Integer(),
  imprints: Type.Integer(),
  /** Empreintes jamais capturées : de l'argent encore saisissable. */
  live_imprints: Type.Integer(),
  last_payment_at: Type.Union([Type.String(), Type.Null()]),
  last_payment_at_formatted: Type.Optional(Type.String()),
  paid_prestations: Type.Integer(),
  payment_types: Type.Array(Type.String()),
  stripe_connect_status: Type.Enum(V1StripeConnectStatus),
});

/** Pourquoi la formule pré-remplie a été remontée (affiché à l'admin, qui garde la main). */
export const PlanUpgradeReasonSchema = Type.Object({
  /** `true` dès que la formule suggérée dépasse le simple mapping du label v1. */
  upgraded: Type.Boolean(),
  /** Clés de motif (`VOUCHERS_STILL_VALID`, `LIVE_IMPRINTS`, `RECENT_VOUCHERS`, `RECENT_PAYMENTS`). */
  reasons: Type.Array(Type.String()),
  /** Formule qu'aurait donnée le seul mapping du label v1, pour le bouton « revenir à … ». */
  plan_before_upgrade: Type.String(),
});

const V2ExistingDomainSchema = Type.Object({
  pro_website_domain_id: Type.Integer(),
  domain: Type.String(),
  is_primary: Type.Boolean(),
  activation_status: Type.String(),
});

const V2ExistingSchema = Type.Object({
  has_pro_subscription: Type.Boolean(),
  already_migrated: Type.Boolean(),
  has_published_website: Type.Boolean(), // garde-fou domaines (§2bis.0)
  existing_domains: Type.Array(V2ExistingDomainSchema),
  fr_domains_count: Type.Integer(), // règle .fr 1er offert
});

const ProposalSchema = Type.Object({
  v2_subscription_label: Type.String(), // STARTER | PRO | MAX
  v2_size: Type.String(), // SOLO | TEAM
  is_smart_ride: Type.Boolean(),
  billing_cycle_anchor_iso: Type.String(),
  v1_monthly_price_tax_free_minor: Type.Integer(), // HT mensuel (déjà /10 si annuel)
  // Décimal obligatoire : le taux vient du régime de TVA du pro, et 8,5 % (DOM) n'est pas entier.
  // Un `Type.Integer()` ici faisait rejeter en 400 toute simulation/exécution de migration d'un
  // pro non métropolitain. Le service arrondit lui-même pour la colonne `Int` héritée.
  vat_applicable_perc: Type.Number(),
});

const ComputedSchema = Type.Object({
  catalogue_ht_minor: Type.Integer(),
  gap_minor: Type.Integer(),
  discounted_ht_minor: Type.Integer(),
  catalogue_ttc_minor: Type.Integer(),
  discounted_ttc_minor: Type.Integer(),
  discount_percentage: Type.Integer(),
  first_v2_charge_at_iso: Type.String(),
  promo_code: Type.String(), // MIGR-<SLUG>
});

// ───────────────────────────── Preview ─────────────────────────────

export const SubscriptionMigrationPreviewResponseSchema = Type.Object({
  stripe_customer: Type.Union([StripeCustomerSchema, Type.Null()]),
  customer_source: Type.Union([
    Type.Literal('MANUAL'),
    Type.Literal('V2_DB'),
    Type.Literal('STRIPE_SEARCH_EMAIL'),
    Type.Literal('NONE'),
  ]),
  candidates: Type.Array(CustomerCandidateSchema),
  v1: Type.Object({
    plan_subscription: Type.Union([V1PlanSubscriptionSchema, Type.Null()]),
    other_subscriptions: Type.Array(V1OtherSubscriptionSchema),
    default_card: Type.Union([DefaultCardSchema, Type.Null()]),
    // `null` = la v1 n'a pas répondu (pro non migré, v1 injoignable) : l'admin voit alors la
    // suggestion nue, sans jamais croire à tort que le pro n'utilise rien.
    plan_usage: Type.Union([V1PlanUsageSchema, Type.Null()]),
  }),
  plan_upgrade: PlanUpgradeReasonSchema,
  v2_existing: V2ExistingSchema,
  proposal: ProposalSchema,
  computed: ComputedSchema,
});

// ───────────────────────────── Simulate ─────────────────────────────

export const SubscriptionMigrationSimulateBodySchema = Type.Object({
  proposal: ProposalSchema,
});

export const SubscriptionMigrationSimulateResponseSchema = Type.Object({
  computed: ComputedSchema,
});

// ───────────────────────────── Execute (formule) ─────────────────────────────

export const SubscriptionMigrationExecuteBodySchema = Type.Object({
  professional_id: Type.Integer(),
  stripe_customer_id: Type.String(),
  stripe_subscription_id_to_cancel: Type.Union([Type.String(), Type.Null()]),
  proposal: ProposalSchema,
  options: Type.Object({
    cancel_v1_subscription: Type.Boolean(),
    create_lifetime_discount: Type.Boolean(),
  }),
});

export const SubscriptionMigrationExecuteResponseSchema = Type.Object({
  success: Type.Boolean(),
  promo_code: Type.Union([Type.String(), Type.Null()]),
  v1_cancel_status: Type.Union(literalUnion(Object.values(V1_CANCEL_STATUS))),
  v1_cancel_error: Type.Union([Type.String(), Type.Null()]),
});

export const SubscriptionMigrationCancelV1BodySchema = Type.Object({
  stripe_subscription_id: Type.String(),
});

export const SubscriptionMigrationCancelV1ResponseSchema = Type.Object({
  v1_cancel_status: Type.Union([Type.Literal('OK'), Type.Literal('FAILED')]),
  v1_cancel_error: Type.Union([Type.String(), Type.Null()]),
});

// ───────────────────────────── Execute (domaines, §2bis.2) ─────────────────────────────

const DomainMigrationInputSchema = Type.Object({
  domain: Type.String(),
  is_primary: Type.Boolean(),
  /** Facturation OPTIONNELLE : les deux champs renseignés = renouvellement programmé ;
   * les deux absents = migration « activation seule » (DNS + HTTPS, `auto_renew` off) — la
   * facturation pourra être attachée plus tard en re-passant le domaine ici.
   * Un seul des deux fourni = rejeté (`error: PARTIAL_BILLING_INPUT` dans le résultat). */
  renewal_price_minor: Type.Optional(Type.Integer()), // HT catalogue v2, éditable ; 0 = .fr offert
  next_renewal_at_iso: Type.Optional(Type.String()),
  v1_stripe_subscription_id_to_cancel: Type.Union([Type.String(), Type.Null()]),
  ovh_order_id: Type.Optional(Type.String()),
});

export const SubscriptionMigrationDomainsExecuteBodySchema = Type.Object({
  professional_id: Type.Integer(),
  domains: Type.Array(DomainMigrationInputSchema),
  options: Type.Object({
    cancel_v1_subscriptions: Type.Boolean(),
  }),
});

const DomainMigrationResultSchema = Type.Object({
  domain: Type.String(),
  pro_website_domain_id: Type.Union([Type.Integer(), Type.Null()]),
  created: Type.Boolean(),
  /** true = domaine déjà migré (ex. auto-migré au cutover) → facturation attachée à la ligne existante. */
  updated: Type.Optional(Type.Boolean()),
  error: Type.Union([Type.String(), Type.Null()]),
  v1_cancel_status: Type.Union(literalUnion(Object.values(V1_CANCEL_STATUS))),
  v1_cancel_error: Type.Union([Type.String(), Type.Null()]),
});

export const SubscriptionMigrationDomainsExecuteResponseSchema = Type.Object({
  results: Type.Array(DomainMigrationResultSchema),
});

export const SubscriptionMigrationDomainCancelV1BodySchema = Type.Object({
  stripe_subscription_id: Type.String(),
});

// ───────────────────────────── Types statiques ─────────────────────────────

export type V1PlanUsage = Static<typeof V1PlanUsageSchema>;
export type PlanUpgradeReason = Static<typeof PlanUpgradeReasonSchema>;
export type SubscriptionMigrationProposal = Static<typeof ProposalSchema>;
export type SubscriptionMigrationComputed = Static<typeof ComputedSchema>;
export type SubscriptionMigrationPreviewResponse = Static<typeof SubscriptionMigrationPreviewResponseSchema>;
export type SubscriptionMigrationSimulateBody = Static<typeof SubscriptionMigrationSimulateBodySchema>;
export type SubscriptionMigrationSimulateResponse = Static<typeof SubscriptionMigrationSimulateResponseSchema>;
export type SubscriptionMigrationExecuteBody = Static<typeof SubscriptionMigrationExecuteBodySchema>;
export type SubscriptionMigrationExecuteResponse = Static<typeof SubscriptionMigrationExecuteResponseSchema>;
export type SubscriptionMigrationCancelV1Body = Static<typeof SubscriptionMigrationCancelV1BodySchema>;
export type SubscriptionMigrationCancelV1Response = Static<typeof SubscriptionMigrationCancelV1ResponseSchema>;
export type SubscriptionMigrationDomainsExecuteBody = Static<typeof SubscriptionMigrationDomainsExecuteBodySchema>;
export type SubscriptionMigrationDomainsExecuteResponse = Static<
  typeof SubscriptionMigrationDomainsExecuteResponseSchema
>;
export type SubscriptionMigrationDomainCancelV1Body = Static<typeof SubscriptionMigrationDomainCancelV1BodySchema>;
